import React, { useEffect, useState } from 'react';
import Modal from '../../../../shared/components/core-ui/modal/modal.component';
import Button, { ButtonType } from '../../../../shared/components/core-ui/button/button.component';
import { InputText } from '../../../../shared/components/core-ui/input-text/input-text.component';
import { TeamMemberUiModel, TeamUiModel } from '../../../../shared/ui-models/team.ui-model';

/**
 * Roles as the team service spells them. Deliberately not `UserRole` — that is
 * the authenticator's job title, this is the member's place in the reporting
 * tree, and conflating them would tie a promotion to an access change.
 */
export const TEAM_ROLES = [
  { value: 'manager', label: 'Manager' },
  { value: 'lead', label: 'Team Lead' },
  { value: 'senior', label: 'Senior Agent' },
  { value: 'agent', label: 'Agent' }
];

export interface MemberFormValue {
  userId: string;
  role: string;
  team: string[];
  reportsTo?: string;
}

interface MemberFormModalProps {
  show: boolean;
  /** Absent means create. Present means edit, and `userId` is then fixed. */
  member?: TeamMemberUiModel;
  teams: TeamUiModel[];
  /** Everyone already in the tree — the candidates for `reportsTo`. */
  members: TeamMemberUiModel[];
  onClose: () => void;
  onSubmit: (value: MemberFormValue) => void;
}

export const MemberFormModal: React.FC<MemberFormModalProps> = ({
  show,
  member,
  teams,
  members,
  onClose,
  onSubmit
}) => {
  const isEdit = Boolean(member);
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState('agent');
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [reportsTo, setReportsTo] = useState('');

  useEffect(() => {
    setUserId(member?.userId ?? '');
    setRole(member?.role ?? 'agent');
    setTeamIds(member?.teamIds ?? []);
    setReportsTo(member?.reportsToId ?? '');
  }, [member, show]);

  // The service rejects a non-manager without a manager, and rejects a manager
  // that has one. Mirroring both here turns a 400 into a disabled button.
  const managerRequired = role !== 'manager';
  const canSubmit =
    userId.trim().length > 0 &&
    teamIds.length > 0 &&
    (managerRequired ? reportsTo.length > 0 : true);

  const toggleTeam = (teamId: string) => {
    setTeamIds(
      teamIds.indexOf(teamId) >= 0
        ? teamIds.filter((id) => id !== teamId)
        : teamIds.concat(teamId)
    );
  };

  return (
    <Modal
      config={{ showModal: show, fullScreen: false, maxWidth: 'sm', showClose: true, autoHeight: true }}
      eventHandlers={{ closeModalHandler: onClose }}
    >
      <div className="flex flex-col gap-4 px-6 py-4">
        <h2 className="text-lg font-semibold">{isEdit ? 'Edit member' : 'Add member'}</h2>

        <div>
          <label className="mb-1 block text-sm text-gray-600">User ID</label>
          <InputText
            data={{ value: userId, placeholder: 'Authenticator user id' }}
            config={{ disabled: isEdit }}
            eventHandlers={{ onChangeHandler: (value: string) => setUserId(value) }}
          />
          {isEdit ? (
            <p className="mt-1 text-xs text-gray-500">
              A member is identified by their user id — change it and you would be moving the row,
              not editing it. Archive and re-add instead.
            </p>
          ) : null}
        </div>

        <div>
          <label className="mb-1 block text-sm text-gray-600">Role</label>
          <select
            className="w-full rounded border border-gray-300 px-3 py-2"
            value={role}
            onChange={(event) => setRole(event.target.value)}
          >
            {TEAM_ROLES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1 block text-sm text-gray-600">Teams</label>
          <div className="flex flex-wrap gap-2">
            {teams.map((team) => (
              <button
                key={team.id}
                type="button"
                onClick={() => toggleTeam(team.id)}
                className={`rounded-full border px-3 py-1 text-sm ${
                  teamIds.indexOf(team.id) >= 0
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-600'
                }`}
              >
                {team.name}
              </button>
            ))}
          </div>
          {teams.length === 0 ? (
            <p className="mt-1 text-xs text-gray-500">No teams yet — create one first.</p>
          ) : null}
        </div>

        <div>
          <label className="mb-1 block text-sm text-gray-600">
            Reports to {managerRequired ? '' : '(managers report to nobody)'}
          </label>
          <select
            className="w-full rounded border border-gray-300 px-3 py-2 disabled:bg-gray-100"
            value={reportsTo}
            disabled={!managerRequired}
            onChange={(event) => setReportsTo(event.target.value)}
          >
            <option value="">Select a manager</option>
            {members
              .filter((candidate) => candidate.userId !== member?.userId)
              .map((candidate) => (
                <option key={candidate.userId} value={candidate.userId}>
                  {candidate.name} — {candidate.role}
                </option>
              ))}
          </select>
        </div>
      </div>

      <div className="flex flex-col gap-3 px-6 pb-6 sm:flex-row sm:justify-end">
        <Button type={ButtonType.gray} onClick={onClose}>
          Cancel
        </Button>
        <Button
          type={ButtonType.primary}
          disabled={!canSubmit}
          tracking={{
            event: 'record_saved',
            entity: 'team_member',
            action: isEdit ? 'update' : 'create',
            outcome: 'success'
          }}
          onClick={() =>
            onSubmit({
              userId: userId.trim(),
              role,
              team: teamIds,
              reportsTo: managerRequired ? reportsTo : undefined
            })
          }
        >
          {isEdit ? 'Save' : 'Add member'}
        </Button>
      </div>
    </Modal>
  );
};
